import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import TablePagination from '@mui/material/TablePagination';
import type { TablePaginationActionsProps } from '@mui/material/TablePagination/TablePaginationActions';
import type React from 'react';

import classes from './Pagination.styles';

export const DEFAULT_PAGE_SIZES = [5, 10, 15, 20, 50, 100];

function TablePaginationActions(props: TablePaginationActionsProps & { currentPageCount?: number }) {
  const {
    count, page, rowsPerPage, onPageChange, currentPageCount,
  } = props;

  const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  let nextButtonDisabled = count === -1 ? false : page >= Math.ceil(count / rowsPerPage) - 1;
  if (count === -1 && currentPageCount !== undefined) {
    if (currentPageCount === 0 || currentPageCount < rowsPerPage) nextButtonDisabled = true;
  }

  return (
    <Box sx={classes.actions}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
        data-e2e-table-pagination="first"
        size="small"
      >
        <FirstPageIcon />
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
        data-e2e-table-pagination="previous"
        size="small"
      >
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={nextButtonDisabled}
        aria-label="next page"
        data-e2e-table-pagination="next"
        size="small"
      >
        <KeyboardArrowRight />
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={count === -1 || page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
        data-e2e-table-pagination="last"
        size="small"
      >
        <LastPageIcon />
      </IconButton>
    </Box>
  );
}

export interface ComponentProps {
  count?: number;
  page: number;
  pageSize: number;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (pageSize: number) => void;
  pageSizes?: number[];
  currentPageCount?: number;
}

function Pagination({
  count, page, pageSize, pageSizes,
  handlePageChange, handlePageSizeChange, currentPageCount,
}: ComponentProps) {
  return (
    <TablePagination
      role="navigation"
      aria-label="Page Navigation"
      component="div"
      rowsPerPageOptions={pageSizes || DEFAULT_PAGE_SIZES}
      count={count === undefined ? -1 : count}
      rowsPerPage={pageSize}
      page={page}
      SelectProps={{
        inputProps: {
          'aria-label': 'rows per page',
          'data-e2e-table-pagination': 'perPage',
        },
      }}
      onPageChange={(e, newPage) => handlePageChange(newPage)}
      onRowsPerPageChange={(e) => handlePageSizeChange(Number(e.target.value))}
      // eslint-disable-next-line react/no-unstable-nested-components
      ActionsComponent={(props) => (
         
        <TablePaginationActions {...props} currentPageCount={currentPageCount} />
      )}
      labelDisplayedRows={count === undefined
        ? (paginationInfo) => (`${paginationInfo.from}-${paginationInfo.to}`)
        : undefined}
    />
  );
}

export default Pagination;
